import { useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useForm, useFormState } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import { Button } from 'common/components/Button';
import { BoxLoader } from 'common/components/Loader';
import { SelectField, SwitchField } from 'common/forms/Field';
import { TableCell } from 'common/components/Table';
import { useApolloError } from 'common/hooks/useApolloError';
import { TSelectOption } from 'common/types';
import { enumToCamelCaseMap } from 'common/utils/enum';
import {
    ExtraCategoriesDocument,
    IContentTypeValue,
    IExtraCategoryColumnInput,
    IImportUserHeader,
    ImportUsersDocument,
} from 'graphql/types';
import { IImportData, EUsersImportType } from 'user/types';
import { matchUserHeadersToOptions } from 'user/utils/csv';
import { userImportFormValidator } from 'user/validators/userImportForm';

const importUserHeaderCamelCaseMap = enumToCamelCaseMap(IImportUserHeader);
const importUserHeaderValues = new Set<string>(
    Object.values(IImportUserHeader)
);
const BLANK_VALUE = '---';

const importTypeToContentType: Partial<
    Record<EUsersImportType, IContentTypeValue>
> = {
    [EUsersImportType.Group]: IContentTypeValue.Group,
    [EUsersImportType.Training]: IContentTypeValue.Training,
    [EUsersImportType.OfferEvent]: IContentTypeValue.OfferEvent,
};

interface IUserImportFormValues {
    includeHeaders: boolean;
    organisationEmail: boolean;
    trainingEmail: boolean;
    headersOrder: string[];
}

const HeadersOrderError = ({
    control,
}: {
    control: ReturnType<typeof useForm<IUserImportFormValues>>['control'];
}) => {
    const { errors } = useFormState({ control, name: 'headersOrder' });
    const message = errors.headersOrder?.root?.message as string | undefined;

    if (!message) return null;

    return (
        <Typography color="error" role="alert" sx={{ mt: 1 }}>
            {message}
        </Typography>
    );
};

interface IProps {
    importData: IImportData;
    importType: EUsersImportType;
    forRelatedIds?: string[];
    showTrainingEmail?: boolean;
    onSubmitSuccess(taskId: string): void;
    onClose(): void;
}

export const UserImportForm = ({
    importData,
    importType,
    forRelatedIds,
    showTrainingEmail,
    onSubmitSuccess,
    onClose,
}: IProps) => {
    const { csvData, fileId } = importData;
    const [translate] = useTranslation();
    const { showApolloError } = useApolloError();
    const [importUsers] = useMutation(ImportUsersDocument);

    const {
        data: extraCategoryData,
        loading: extraCategoryLoading,
        error: extraCategoryError,
    } = useQuery(ExtraCategoriesDocument, {
        variables: { contentType: IContentTypeValue.User },
    });

    useEffect(() => {
        if (extraCategoryError) {
            showApolloError(extraCategoryError);
        }
    }, [extraCategoryError, showApolloError]);

    const userExtraCategories = useMemo(
        () => extraCategoryData?.extraCategories ?? [],
        [extraCategoryData]
    );

    const headerOptions = useMemo<TSelectOption[]>(() => {
        const builtInOptions = Object.values(IImportUserHeader)
            .filter(
                (value) =>
                    value !== IImportUserHeader.Ignore &&
                    value !== IImportUserHeader.ExtraCategory
            )
            .map((value) => ({
                label: translate(
                    `userImportModal.header.${importUserHeaderCamelCaseMap[value]}`
                ),
                value,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        const extraCategoryOptions = userExtraCategories
            .map((category) => ({
                label: category.name,
                value: category.id,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        return [
            { label: BLANK_VALUE, value: IImportUserHeader.Ignore },
            ...builtInOptions,
            ...(extraCategoryOptions.length
                ? [
                      {
                          label: '',
                          value: IImportUserHeader.Ignore,
                          divider: true,
                      },
                  ]
                : []),
            ...extraCategoryOptions,
        ];
    }, [translate, userExtraCategories]);

    const defaultValues = useMemo(
        () => ({
            includeHeaders: !!csvData.hasHeaders,
            organisationEmail: true,
            trainingEmail:
                importType === EUsersImportType.Group ||
                importType === EUsersImportType.Training ||
                !!showTrainingEmail,
            headersOrder: csvData.hasHeaders
                ? matchUserHeadersToOptions(
                      csvData.headers,
                      headerOptions.filter((option) => !option.divider)
                  )
                : csvData.headers.map(() => IImportUserHeader.Ignore),
        }),
        [csvData, headerOptions, importType, showTrainingEmail]
    );

    const { control, handleSubmit, reset, watch } = useForm({
        resolver: zodResolver(userImportFormValidator),
        defaultValues,
    });

    useEffect(() => {
        reset(defaultValues);
    }, [defaultValues, reset]);

    const includeHeaders = watch('includeHeaders');

    const displayRows = useMemo(() => {
        if (includeHeaders) return csvData.rows;

        return [csvData.headers, ...csvData.rows];
    }, [csvData, includeHeaders]);

    const hasTrainingEmail =
        importType === EUsersImportType.Group ||
        importType === EUsersImportType.Training ||
        !!showTrainingEmail;

    const onSubmit = handleSubmit(async (values) => {
        const forRelatedType = importTypeToContentType[importType];

        const headersOrder: IImportUserHeader[] = [];
        const extraCategoryColumns: IExtraCategoryColumnInput[] = [];

        values.headersOrder.forEach((value, columnIndex) => {
            // headersOrder mixes built-in headers and extra category UUIDs.
            if (importUserHeaderValues.has(value)) {
                headersOrder.push(value as IImportUserHeader);
            } else {
                headersOrder.push(IImportUserHeader.ExtraCategory);
                extraCategoryColumns.push({
                    columnIndex,
                    categoryId: value,
                });
            }
        });

        try {
            const result = await importUsers({
                variables: {
                    fileId,
                    headersOrder,
                    extraCategoryColumns: extraCategoryColumns.length
                        ? extraCategoryColumns
                        : undefined,
                    includeHeaders: values.includeHeaders,
                    organisationEmail: values.organisationEmail,
                    trainingEmail: hasTrainingEmail
                        ? values.trainingEmail
                        : undefined,
                    forRelatedType,
                    forRelatedIds:
                        forRelatedType && forRelatedIds?.length
                            ? forRelatedIds
                            : undefined,
                },
            });

            const taskId = result.data?.importUsers?.taskId;

            if (taskId) {
                onSubmitSuccess(taskId);
            }
        } catch (error) {
            showApolloError(error);
        }
    });

    if (extraCategoryLoading) {
        return <BoxLoader sx={{ height: 100 }} />;
    }

    return (
        <form style={{ width: '100%' }} onSubmit={onSubmit}>
            <Typography>{translate('userImportModal.description')}</Typography>

            <SwitchField
                control={control}
                label={translate('userImportModal.includeHeaders')}
                name="includeHeaders"
            />

            <HeadersOrderError control={control} />

            <TableContainer
                sx={{
                    my: 2,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                }}
            >
                <Table
                    sx={{
                        'td, th': {
                            borderRight: '1px solid',
                            borderColor: 'divider',
                            px: 2,
                            py: 2,
                            '&:last-child': {
                                borderRight: 'none',
                            },
                        },
                    }}
                >
                    <TableBody>
                        {csvData.headers.map((header, colIndex) => {
                            const maxDisplay = 1;
                            const values = displayRows.map(
                                (row) => row[colIndex] ?? ''
                            );

                            return (
                                <TableRow key={`col-${header}-${colIndex}`}>
                                    <TableCell sx={{ width: 200 }}>
                                        <SelectField
                                            control={control}
                                            margin="none"
                                            name={`headersOrder.${colIndex}`}
                                            options={headerOptions}
                                            size="small"
                                            sx={{
                                                width: 200,
                                            }}
                                        />
                                    </TableCell>

                                    <TableCell
                                        sx={{
                                            bgcolor: 'grey.100',
                                            fontWeight: 'bold',
                                            width: 70,
                                            minWidth: 70,
                                        }}
                                    >
                                        {includeHeaders ? header : BLANK_VALUE}
                                    </TableCell>

                                    {values
                                        .slice(0, maxDisplay)
                                        .map((value, rowIndex) => (
                                            <TableCell
                                                key={`cell-${colIndex}-${rowIndex}`}
                                            >
                                                {value}
                                            </TableCell>
                                        ))}

                                    <TableCell
                                        sx={{
                                            width: 70,
                                            minWidth: 70,
                                        }}
                                    >
                                        {BLANK_VALUE}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <SwitchField
                control={control}
                formControlProps={{
                    margin: 'none',
                }}
                label={translate('userImportModal.organisationEmail')}
                name="organisationEmail"
            />

            {hasTrainingEmail && (
                <SwitchField
                    control={control}
                    formControlProps={{
                        margin: 'none',
                    }}
                    label={translate(
                        importType === EUsersImportType.Group
                            ? 'userImportModal.groupTrainingEmail'
                            : 'userImportModal.trainingEmail'
                    )}
                    name="trainingEmail"
                />
            )}

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 2 }}>
                <Button color="primary" type="submit" variant="contained">
                    {translate('userImportModal.import')}
                </Button>
                <Button color="inherit" variant="contained" onClick={onClose}>
                    {translate('cancel')}
                </Button>
            </Box>
        </form>
    );
};
